import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Users, MapPin, Globe, Check, Plus, Heart, MessageCircle, Send, Shield, Calendar, Flame, Loader2, ImagePlus } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EventCard, ChallengeCard, ProjectCard } from "@/components/cards";
import { startCheckout } from "@/lib/checkout";
import { Loader, EmptyState, PassionBadge, PrimaryButton, GhostButton, money } from "@/components/ui-bits";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import ImageUpload from "@/components/ImageUpload";
import { toast } from "sonner";

function Composer({ communityId, onPost }) {
  const [text, setText] = useState("");
  const [image, setImage] = useState("");
  const [busy, setBusy] = useState(false);
  const [expand, setExpand] = useState(false);
  const submit = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/posts", { community_id: communityId, text: text.trim(), image });
      onPost(data); setText(""); setImage(""); setExpand(false);
      toast.success("Posted to the community");
    } catch { toast.error("Could not post"); } finally { setBusy(false); }
  };
  return (
    <div className="pa-card p-4 mb-5" data-testid="post-composer">
      <textarea value={text} onFocus={() => setExpand(true)} onChange={(e) => setText(e.target.value)} rows={expand ? 3 : 1}
        placeholder="Share something with the community…" data-testid="post-input"
        className="w-full resize-none bg-transparent text-[#071126] placeholder:text-[#55708F]/70 outline-none text-sm" />
      {expand && (
        <>
          {image ? <div className="mt-3"><ImageUpload value={image} onChange={setImage} aspect="aspect-video" testid="post-image" /></div> : null}
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#071126]/8">
            {!image ? <ImageUploadInline onChange={setImage} /> : <span className="text-xs text-[#55708F]">Image attached</span>}
            <div className="flex gap-2">
              <button onClick={() => { setExpand(false); setText(""); setImage(""); }} className="text-sm text-[#55708F] px-3">Cancel</button>
              <PrimaryButton onClick={submit} disabled={busy || !text.trim()} data-testid="post-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Post"}</PrimaryButton>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function ImageUploadInline({ onChange }) {
  const [show, setShow] = useState(false);
  if (show) return <div className="w-40"><ImageUpload value="" onChange={onChange} aspect="aspect-video" label="Add photo" testid="post-image" /></div>;
  return <button onClick={() => setShow(true)} className="inline-flex items-center gap-1.5 text-sm text-[#55708F] hover:text-[#FF6B00]" data-testid="post-add-image"><ImagePlus className="h-4 w-4" />Add photo</button>;
}

function PostCard({ p, onLike }) {
  const [showC, setShowC] = useState(false);
  const [comments, setComments] = useState(null);
  const [text, setText] = useState("");
  const { user } = useAuth();
  const load = async () => { const { data } = await api.get(`/posts/${p.id}/comments`); setComments(data); };
  const toggle = () => { setShowC((v) => { if (!v && !comments) load(); return !v; }); };
  const addComment = async () => {
    if (!text.trim()) return;
    const { data } = await api.post(`/posts/${p.id}/comments`, { text: text.trim() });
    setComments((c) => [...(c || []), data]); setText(""); p.comment_count = (p.comment_count || 0) + 1;
  };
  return (
    <div className="pa-card p-5" data-testid={`post-${p.id}`}>
      <div className="flex items-center gap-3 mb-3">
        <img src={fileUrl(p.author?.avatar)} alt="" className="h-10 w-10 rounded-full object-cover" />
        <div><p className="font-semibold text-[#071126] text-sm">{p.author?.name}</p></div>
      </div>
      <p className="text-[#071126]/85 text-sm leading-relaxed whitespace-pre-wrap">{p.text}</p>
      {p.image && <img src={fileUrl(p.image)} alt="" className="mt-3 rounded-xl w-full max-h-96 object-cover" />}
      <div className="flex items-center gap-4 mt-3 text-sm text-[#55708F]">
        <button onClick={() => onLike(p)} className="inline-flex items-center gap-1.5 hover:text-[#FF6B00] transition-colors" data-testid={`post-like-${p.id}`}>
          <Heart className={`h-4 w-4 ${p.liked ? "fill-[#FF6B00] text-[#FF6B00]" : ""}`} />{p.like_count}
        </button>
        <button onClick={toggle} className="inline-flex items-center gap-1.5 hover:text-[#FF6B00]" data-testid={`post-comment-${p.id}`}><MessageCircle className="h-4 w-4" />{p.comment_count}</button>
      </div>
      {showC && (
        <div className="mt-4 pt-4 border-t border-[#071126]/8 space-y-3">
          {comments?.map((c) => (
            <div key={c.id} className="flex gap-2.5">
              <img src={fileUrl(c.author?.avatar)} alt="" className="h-8 w-8 rounded-full object-cover" />
              <div className="bg-[#FFFDF8] rounded-2xl px-3 py-2 flex-1"><p className="text-xs font-semibold text-[#071126]">{c.author?.name}</p><p className="text-sm text-[#071126]/80">{c.text}</p></div>
            </div>
          ))}
          <div className="flex gap-2 items-center">
            <img src={fileUrl(user?.avatar)} alt="" className="h-8 w-8 rounded-full object-cover" />
            <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addComment()} placeholder="Write a comment…" data-testid={`comment-input-${p.id}`}
              className="flex-1 rounded-full bg-[#FFFDF8] border border-[#071126]/10 px-4 py-2 text-sm outline-none focus:border-[#FF6B00]/50" />
            <button onClick={addComment} className="h-9 w-9 grid place-items-center rounded-full bg-[#FF6B00] text-white" data-testid={`comment-submit-${p.id}`}><Send className="h-4 w-4" /></button>
          </div>
        </div>
      )}
    </div>
  );
}

const TABS = ["overview", "posts", "events", "challenges", "projects", "members"];

export default function CommunityDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [c, setC] = useState(null);
  const [tab, setTab] = useState("overview");
  const [posts, setPosts] = useState(null);
  const [events, setEvents] = useState(null);
  const [challenges, setChallenges] = useState(null);
  const [projects, setProjects] = useState(null);
  const [members, setMembers] = useState(null);
  const [joining, setJoining] = useState(false);

  const load = () => api.get(`/communities/${id}`).then((r) => setC(r.data));
  useEffect(() => { setC(null); load(); }, [id]);

  useEffect(() => {
    if (!c) return;
    if (tab === "posts" && !posts) api.get(`/posts?community_id=${id}`).then((r) => setPosts(r.data));
    if (tab === "events" && !events) api.get(`/events?community_id=${id}`).then((r) => setEvents(r.data));
    if (tab === "challenges" && !challenges) api.get(`/challenges?community_id=${id}`).then((r) => setChallenges(r.data));
    if (tab === "projects" && !projects) api.get(`/projects?passion=${c.passion}`).then((r) => setProjects(r.data));
    if (tab === "members" && !members) api.get(`/communities/${id}/members`).then((r) => setMembers(r.data));
    if (tab === "overview" && !posts) api.get(`/posts?community_id=${id}`).then((r) => setPosts(r.data));
  }, [tab, c]); // eslint-disable-line

  const join = async () => {
    setJoining(true);
    try {
      if (c.is_member) { await api.post(`/communities/${id}/leave`); toast("Left the community"); }
      else { await api.post(`/communities/${id}/join`); toast.success(`Welcome to ${c.name}!`); }
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); } finally { setJoining(false); }
  };

  const likePost = async (p) => {
    const { data } = await api.post(`/posts/${p.id}/like`);
    setPosts((ps) => ps.map((x) => x.id === p.id ? { ...x, liked: data.liked, like_count: data.like_count } : x));
  };

  if (!c) return <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"><Loader /></div>;
  const isOwner = user?.id === c.owner_id;

  return (
    <div>
      <div className="relative h-52 sm:h-64">
        <img src={fileUrl(c.cover)} alt={c.name} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#071126] to-[#071126]/20" />
      </div>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 -mt-16 relative">
        <div className="pa-card p-6">
          <div className="flex flex-col sm:flex-row sm:items-start gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2"><PassionBadge slug={c.passion} />{c.is_private && <span className="text-xs px-2.5 py-1 rounded-full bg-[#071126]/8 text-[#071126]/70 font-semibold">Private</span>}</div>
              <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-[#071126]">{c.name}</h1>
              <p className="text-[#071126]/60 mt-1">{c.tagline}</p>
              <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-[#55708F] font-medium">
                <span className="inline-flex items-center gap-1.5"><Users className="h-4 w-4" />{c.member_count} members</span>
                <span className="inline-flex items-center gap-1.5">{c.location === "Online" ? <Globe className="h-4 w-4" /> : <MapPin className="h-4 w-4" />}{c.location || "Global"}</span>
                <span className="inline-flex items-center gap-1.5">Led by <Link to={`/u/${c.owner?.id}`} className="text-[#FF6B00] hover:underline">{c.owner?.name}</Link></span>
              </div>
            </div>
            <div className="flex gap-2">
              {isOwner ? (
                <GhostButton as={Link} to="/dashboard" data-testid="manage-community-btn"><Shield className="h-4 w-4" />Manage</GhostButton>
              ) : c.is_member ? (
                <GhostButton onClick={join} disabled={joining} data-testid="leave-community-btn">{joining ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="h-4 w-4" />Joined</>}</GhostButton>
              ) : c.membership_price > 0 ? (
                <PrimaryButton onClick={() => startCheckout("membership", c.id)} data-testid="join-membership-btn"><Plus className="h-4 w-4" />Join · {money(c.membership_price)}</PrimaryButton>
              ) : (
                <PrimaryButton onClick={join} disabled={joining} data-testid="join-community-btn">{joining ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4" />Join community</>}</PrimaryButton>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="h-auto p-1 bg-[#071126]/5 rounded-xl flex flex-wrap gap-1 mb-6">
            {TABS.map((t) => <TabsTrigger key={t} value={t} data-testid={`tab-${t}`} className="capitalize rounded-lg data-[state=active]:bg-white data-[state=active]:text-[#FF6B00] px-4 py-2 text-sm font-medium">{t}</TabsTrigger>)}
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            {c.membership_price > 0 && (
              <div className="pa-card p-6 border-[#FF6B00]/30 bg-gradient-to-br from-[#FF6B00]/5 to-transparent" data-testid="membership-card">
                <div className="flex items-center gap-2 mb-2"><span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-[#FFC94A]/25 text-[#071126]">Premium membership</span><span className="font-bold text-[#FF6B00]">{money(c.membership_price)}</span></div>
                <h3 className="font-semibold text-[#071126]">Support this community &amp; unlock perks</h3>
                {c.membership_perks?.length > 0 && <ul className="mt-3 space-y-1.5">{c.membership_perks.map((p, i) => <li key={i} className="flex gap-2 text-sm text-[#071126]/75"><Check className="h-4 w-4 text-[#8FAF9B] flex-shrink-0 mt-0.5" />{p}</li>)}</ul>}
                {!c.is_member && !isOwner && <PrimaryButton onClick={() => startCheckout("membership", c.id)} className="mt-4" data-testid="overview-join-membership">Become a member · {money(c.membership_price)}</PrimaryButton>}
              </div>
            )}
            <div className="pa-card p-6"><h3 className="font-semibold text-[#071126] mb-2">About this community</h3><p className="text-[#071126]/70 leading-relaxed whitespace-pre-wrap">{c.description}</p>
              {c.tags?.length > 0 && <div className="flex flex-wrap gap-2 mt-4">{c.tags.map((t) => <span key={t} className="px-3 py-1 rounded-full bg-[#071126]/5 text-xs text-[#071126]/60 font-medium">#{t}</span>)}</div>}
            </div>
            {c.rules?.length > 0 && <div className="pa-card p-6"><h3 className="font-semibold text-[#071126] mb-3">Community rules</h3><ol className="space-y-2">{c.rules.map((r, i) => <li key={i} className="flex gap-3 text-sm text-[#071126]/70"><span className="h-5 w-5 rounded-full bg-[#FF6B00]/10 text-[#FF6B00] grid place-items-center text-xs font-bold flex-shrink-0">{i + 1}</span>{r}</li>)}</ol></div>}
            <div><h3 className="font-semibold text-[#071126] mb-3">Recent posts</h3>{posts?.length ? <div className="space-y-4">{posts.slice(0, 3).map((p) => <PostCard key={p.id} p={p} onLike={likePost} />)}</div> : <EmptyState icon={MessageCircle} title="No posts yet" hint="Start the conversation." />}</div>
          </TabsContent>

          <TabsContent value="posts">
            {c.is_member && <Composer communityId={id} onPost={(p) => setPosts((ps) => [p, ...(ps || [])])} />}
            {!posts ? <Loader /> : posts.length ? <div className="space-y-4">{posts.map((p) => <PostCard key={p.id} p={p} onLike={likePost} />)}</div>
              : <EmptyState icon={MessageCircle} title="No posts yet" hint={c.is_member ? "Be the first to share." : "Join to start posting."} />}
          </TabsContent>

          <TabsContent value="events">
            {!events ? <Loader /> : events.length ? <div className="grid gap-5 sm:grid-cols-2">{events.map((e) => <EventCard key={e.id} e={e} />)}</div>
              : <EmptyState icon={Calendar} title="No upcoming events" hint="Create an event for your community." action={isOwner && <PrimaryButton as={Link} to="/create/event">Create event</PrimaryButton>} />}
          </TabsContent>

          <TabsContent value="challenges">
            {!challenges ? <Loader /> : challenges.length ? <div className="grid gap-5 sm:grid-cols-2">{challenges.map((c2) => <ChallengeCard key={c2.id} c={c2} />)}</div>
              : <EmptyState icon={Flame} title="No active challenges" hint="Launch a challenge to energize your members." action={isOwner && <PrimaryButton as={Link} to="/create/challenge">Create challenge</PrimaryButton>} />}
          </TabsContent>

          <TabsContent value="projects">
            {!projects ? <Loader /> : projects.length ? <div className="grid gap-5 grid-cols-2 lg:grid-cols-4">{projects.map((p) => <ProjectCard key={p.id} p={p} />)}</div>
              : <EmptyState icon={ImagePlus} title="No projects yet" hint="Share what you're making." />}
          </TabsContent>

          <TabsContent value="members">
            {!members ? <Loader /> : (
              <div className="grid gap-3 sm:grid-cols-2">
                {members.map((m) => (
                  <Link key={m.user.id} to={`/u/${m.user.id}`} className="pa-card pa-card-hover p-4 flex items-center gap-3" data-testid={`member-${m.user.id}`}>
                    <img src={fileUrl(m.user.avatar)} alt="" className="h-11 w-11 rounded-full object-cover" />
                    <div className="min-w-0 flex-1"><p className="font-semibold text-[#071126] truncate">{m.user.name}</p><p className="text-xs text-[#071126]/55 line-clamp-1">{m.user.bio}</p></div>
                    {m.role === "owner" && <span className="text-xs px-2.5 py-1 rounded-full bg-[#FFC94A]/25 text-[#071126] font-semibold">Owner</span>}
                  </Link>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
